import { ReactNode } from "react";
import {
  Landmark, Building2, Shield, Bot, Users, Star, BarChart3,
  AlertTriangle, Scale, Search, Calculator, Zap, TrendingUp,
  MessageSquare, Calendar, BookOpen, Newspaper, Lock, Globe,
  CreditCard, FileText, Cpu, Eye, Gavel
} from "lucide-react";

export interface NavSubItem {
  label: string;
  href: string;
  icon: React.ElementType;
  description?: string;
}

export interface NavGroup {
  label: string;
  items: NavSubItem[];
}

export interface MegaMenuItem {
  label: string;
  groups: NavGroup[];
}

export const MEGA_MENU: MegaMenuItem[] = [
  {
    label: "Brokers",
    groups: [
      {
        label: "Explore",
        items: [
          { label: "All Brokers", href: "/brokers", icon: Landmark, description: "Browse the full broker directory" },
          { label: "Broker Reviews", href: "/brokers/reviews", icon: Star, description: "Verified user reviews" },
          { label: "Broker Comparisons", href: "/compare/brokers", icon: BarChart3, description: "Side-by-side comparison tables" },
        ],
      },
      {
        label: "Tools",
        items: [
          { label: "Regulation Check", href: "/brokers/regulation-check", icon: Shield, description: "Verify broker licenses" },
          { label: "Fee Analyzer", href: "/brokers/fee-analyzer", icon: Calculator, description: "Compare true trading costs" },
          { label: "Broker Matchmaker", href: "/brokers/matchmaker", icon: Search, description: "Find your ideal broker" },
        ],
      },
    ],
  },
  {
    label: "Prop Firms",
    groups: [
      {
        label: "Explore",
        items: [
          { label: "All Prop Firms", href: "/prop-firms", icon: Building2, description: "Browse funded trading firms" },
          { label: "Firm Reviews", href: "/prop-firms/reviews", icon: Star, description: "Trader-verified reviews" },
          { label: "Firm Comparisons", href: "/compare/prop-firms", icon: BarChart3, description: "Compare evaluation programs" },
        ],
      },
      {
        label: "Tools",
        items: [
          { label: "Challenge Calculator", href: "/prop-firms/calculator", icon: Calculator, description: "Find challenges you can pass" },
          { label: "Payout Tracker", href: "/prop-firms/payouts", icon: CreditCard, description: "Real-time payout monitoring" },
          { label: "Application Tracker", href: "/prop-firms/applications", icon: FileText, description: "Track your applications" },
        ],
      },
    ],
  },
  {
    label: "Scams & Safety",
    groups: [
      {
        label: "Alerts",
        items: [
          { label: "Scam Alert Center", href: "/safety/scams", icon: AlertTriangle, description: "Report and track fraud" },
          { label: "Blacklist / Whitelist", href: "/safety/lists", icon: Shield, description: "Community trust lists" },
          { label: "Legal Actions", href: "/safety/legal", icon: Scale, description: "Fines, lawsuits & enforcement" },
        ],
      },
      {
        label: "Research",
        items: [
          { label: "Due Diligence Tools", href: "/safety/due-diligence", icon: Search, description: "Vet before you invest" },
          { label: "Withdrawal Tracker", href: "/safety/withdrawals", icon: Zap, description: "User-reported payout data" },
          { label: "Insurance Finder", href: "/safety/insurance", icon: Lock, description: "Fund protection info" },
        ],
      },
    ],
  },
  {
    label: "Regulation",
    groups: [
      {
        label: "By Type",
        items: [
          { label: "All Regulators", href: "/regulation", icon: Gavel, description: "Complete global directory" },
          { label: "Tier-1 Regulators", href: "/regulation?type=tier-1", icon: Shield, description: "FCA, ASIC, CySEC & more" },
          { label: "Tier-2 Regulators", href: "/regulation?type=tier-2", icon: Scale, description: "Emerging market authorities" },
          { label: "Offshore Regulators", href: "/regulation?type=offshore", icon: AlertTriangle, description: "CIMA, FSRA, NFSA & more" },
          { label: "Regional Regulators", href: "/regulation?type=regional", icon: Globe, description: "BAPPEBTI, SEBI & more" },
        ],
      },
      {
        label: "By Region",
        items: [
          { label: "Europe", href: "/regulation?region=Europe", icon: Globe, description: "FCA, BaFin, CySEC, CNMV" },
          { label: "Asia-Pacific", href: "/regulation?region=Asia-Pacific", icon: Globe, description: "ASIC, MAS, FSA Japan" },
          { label: "Middle East", href: "/regulation?region=Middle+East", icon: Globe, description: "DFSA, FSRA, CMA" },
          { label: "Caribbean", href: "/regulation?region=Caribbean", icon: Globe, description: "CIMA, FSC, FSRA, NFSA" },
          { label: "Africa", href: "/regulation?region=Africa", icon: Globe, description: "FSCA, CMA Kenya" },
        ],
      },
      {
        label: "News & Tools",
        items: [
          { label: "Regulatory News", href: "/regulation/news", icon: Newspaper, description: "Warnings, fines & rule changes" },
          { label: "Compare Regulators", href: "/regulation/compare", icon: BarChart3, description: "Side-by-side comparison" },
        ],
      },
    ],
  },
  {
    label: "AI Tools",
    groups: [
      {
        label: "Intelligence",
        items: [
          { label: "AI Trading Assistant", href: "/ai/assistant", icon: Bot, description: "Ask anything about forex" },
          { label: "Sentiment Analyzer", href: "/ai/sentiment", icon: TrendingUp, description: "Real-time market sentiment" },
          { label: "Technology Tracker", href: "/ai/tech", icon: Cpu, description: "Who's adopting what" },
        ],
      },
      {
        label: "More",
        items: [
          { label: "Automated News", href: "/ai/news", icon: Newspaper, description: "AI-powered news generation" },
          { label: "Future of Finance", href: "/ai/future", icon: Globe, description: "Trends & predictions" },
          { label: "API Access", href: "/ai/api", icon: Eye, description: "Developer documentation" },
        ],
      },
    ],
  },
  {
    label: "Community",
    groups: [
      {
        label: "Connect",
        items: [
          { label: "Forums", href: "/community/forums", icon: MessageSquare, description: "Discuss with fellow traders" },
          { label: "Verified Reviews", href: "/community/reviews", icon: Star, description: "Authenticated user reviews" },
          { label: "User Rankings", href: "/community/rankings", icon: Users, description: "Top contributors" },
        ],
      },
      {
        label: "Learn",
        items: [
          { label: "Education Center", href: "/education", icon: BookOpen, description: "Guides for all levels" },
          { label: "Live Analysis", href: "/community/live", icon: TrendingUp, description: "Real-time market coverage" },
          { label: "Events Calendar", href: "/community/events", icon: Calendar, description: "Webinars & conferences" },
        ],
      },
    ],
  },
];
